import { useState, useEffect, useCallback } from 'react';
import { BadgeCheck, Eye } from 'lucide-react';
import { subscriptionsApi, Subscription } from '../../api/payments';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader } from '../../components/ui/sheet';
import { Pagination } from '../../components/shared/Pagination';
import {
  EmptyState,
  ErrorState,
  FilterChips,
  FormField,
  PageHeader,
  StatusPill,
  Toolbar,
  type FilterChip, UserLink } from '@/components/app';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { SUBSCRIPTION_STATUS_LABELS, labelOf } from '@/lib/labels';
import { subscriptionStatusTone } from '@/lib/status';
import {
  RowsSkeleton,
  SearchInput,
  SheetItem,
  SheetSection,
  SheetSkeleton,
  formatDateTime,
  shortId,
} from '../Orders/listParts';

const ITEMS_PER_PAGE = 10;
const STATUSES = ['active', 'expired', 'cancelled'] as const;

function SubscriptionStatus({ status }: { status: string }) {
  return (
    <StatusPill tone={subscriptionStatusTone(status)} dot>
      {labelOf(SUBSCRIPTION_STATUS_LABELS, status)}
    </StatusPill>
  );
}

const isExpired = (endsAt: string) => new Date(endsAt) < new Date();

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  // Детали подписки — правая панель.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Subscription | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const fetchSubscriptions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const params: any = { page: currentPage, limit: ITEMS_PER_PAGE };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await subscriptionsApi.getAll(params);
      setSubscriptions(response.items);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить подписки'));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => { fetchSubscriptions(); }, [fetchSubscriptions]);

  // Запрос уходит через 300 мс после последнего символа; страница сбрасывается на первую.
  const applySearch = useDebouncedCallback((value: string) => { setSearchQuery(value); setCurrentPage(1); });
  const handleSearch = (value: string) => { setSearchInput(value); applySearch(value); };
  const handleStatusFilter = (value: string) => { setStatusFilter(value); setCurrentPage(1); };
  const clearSearch = () => { setSearchInput(''); setSearchQuery(''); setCurrentPage(1); };
  const resetFilters = () => { clearSearch(); setStatusFilter('all'); };

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      setDetail(await subscriptionsApi.getById(id));
    } catch (error: any) {
      setDetailError(getErrorMessage(error, 'Не удалось загрузить детали подписки'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleView = (subscription: Subscription) => {
    setDetailId(subscription.id);
    setDetail(null);
    void loadDetail(subscription.id);
  };

  const chips: FilterChip[] = [
    ...(searchQuery ? [{ key: 'search', label: `Поиск: ${searchQuery}`, onRemove: clearSearch, removeLabel: 'Убрать фильтр: поиск' }] : []),
    ...(statusFilter !== 'all'
      ? [{
          key: 'status',
          label: `Статус: ${labelOf(SUBSCRIPTION_STATUS_LABELS, statusFilter)}`,
          onRemove: () => handleStatusFilter('all'),
          removeLabel: 'Убрать фильтр: статус',
        }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Подписки" subtitle="Оплаченный доступ к Premium: срок и статус" />

      <Card role="search" aria-label="Фильтры подписок" className="grid grid-cols-1 gap-3 px-[18px] py-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <FormField label="Поиск" tone="filter">
          <SearchInput
            placeholder="Имя, email, телефон или ID подписки"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </FormField>
        <FormField label="Статус" tone="filter">
          {(p) => (
            <Select value={statusFilter} onValueChange={handleStatusFilter}>
              <SelectTrigger {...p}>
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{SUBSCRIPTION_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
      </Card>

      <Card className="overflow-hidden" role="region" aria-label="Список подписок">
        <Toolbar title={<>Найдено <span className="tabular-nums">{total.toLocaleString('ru-RU')}</span></>}>
          <FilterChips chips={chips} onReset={resetFilters} />
        </Toolbar>

        {isLoading ? (
          <RowsSkeleton label="Загрузка подписок" />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={fetchSubscriptions} className="border-t border-border-soft" />
        ) : subscriptions.length === 0 ? (
          <EmptyState
            className="border-t border-border-soft"
            icon={<BadgeCheck />}
            title="Подписки не найдены"
            description={chips.length ? 'Попробуйте изменить или сбросить фильтры.' : undefined}
            action={chips.length ? <Button variant="secondary" onClick={resetFilters}>Сбросить фильтры</Button> : undefined}
          />
        ) : (
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead>Подписка</TableHead>
                <TableHead>Пользователь</TableHead>
                <TableHead className="hidden md:table-cell">Тариф</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="hidden lg:table-cell">Начало</TableHead>
                <TableHead>Окончание</TableHead>
                <TableHead className="text-right"><span className="sr-only">Действия</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((subscription) => {
                const expired = isExpired(subscription.ends_at);
                return (
                  <TableRow key={subscription.id} selected={detailId === subscription.id}>
                    <TableCell className="font-mono text-xs text-text-2">{shortId(subscription.id)}</TableCell>
                    <TableCell><UserLink user={subscription.user} /></TableCell>
                    <TableCell className="hidden md:table-cell">{subscription.order?.plan?.title || '—'}</TableCell>
                    <TableCell><SubscriptionStatus status={subscription.status} /></TableCell>
                    <TableCell className="hidden whitespace-nowrap tabular-nums text-text-2 lg:table-cell">{formatDateTime(subscription.starts_at)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className={cn('whitespace-nowrap tabular-nums', expired && 'text-neg')}>{formatDateTime(subscription.ends_at)}</span>
                        {expired && <span className="text-xs font-semibold text-neg">Срок истёк</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="secondary" size="icon-sm" aria-label={`Детали подписки ${shortId(subscription.id)}`} onClick={() => handleView(subscription)}>
                        <Eye />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {!isLoading && !loadError && subscriptions.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} total={total} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
        )}
      </Card>

      <Sheet open={detailId !== null} onOpenChange={(open) => { if (!open) setDetailId(null); }}>
        <SheetContent>
          <SheetHeader title="Детали подписки" />
          <SheetDescription className="sr-only">Полная информация о подписке</SheetDescription>
          <SheetBody>
            {detailLoading ? (
              <SheetSkeleton />
            ) : detailError ? (
              <ErrorState message={detailError} onRetry={() => detailId && void loadDetail(detailId)} />
            ) : detail ? (
              <>
                <section aria-label="Итог" className="flex items-start justify-between gap-3 rounded-card border border-border-soft bg-surface px-4 py-3.5 shadow-card">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-xs text-text-2">{detail.user?.username || 'Без имени'}</span>
                    <b className="font-display text-lg font-bold">
                      Premium до <span className="tabular-nums">{formatDateTime(detail.ends_at)}</span>
                    </b>
                    <span className="text-[13px] text-text-2">{detail.order?.plan?.title || 'Тариф не указан'}</span>
                    {isExpired(detail.ends_at) && <span className="text-[13px] font-semibold text-neg">Подписка истекла</span>}
                  </div>
                  <SubscriptionStatus status={detail.status} />
                </section>
                <SheetSection title="Подписка">
                  <SheetItem label="ID подписки" mono wide>{detail.id}</SheetItem>
                  <SheetItem label="Пользователь" wide><UserLink user={detail.user} /></SheetItem>
                  <SheetItem label="Тариф">{detail.order?.plan?.title || '—'}</SheetItem>
                </SheetSection>
                <SheetSection title="Даты">
                  <SheetItem label="Начало"><span className="tabular-nums">{formatDateTime(detail.starts_at)}</span></SheetItem>
                  <SheetItem label="Окончание">
                    <span className={cn('tabular-nums', isExpired(detail.ends_at) && 'text-neg')}>{formatDateTime(detail.ends_at)}</span>
                  </SheetItem>
                  <SheetItem label="Создана"><span className="tabular-nums">{formatDateTime(detail.created_at)}</span></SheetItem>
                </SheetSection>
              </>
            ) : null}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}
